import React, { useEffect, useState, useCallback, useRef } from 'react';
import Dialog from "../../components/controls/Dialog";
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import * as llmModelService from '../../services/llmModelService';
import useAlert from '../../components/useAlert';
import { 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow, 
    Paper, 
    IconButton, 
    FormControl, 
    InputLabel, 
    Select, 
    MenuItem, 
    Box,
    Typography
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import AddIcon from '@mui/icons-material/Add';

const ProjectLlmModelsDialog = (props) => {
    const { open, setOpen, selectedRow, setSelectedRow } = props;
    const [projectModels, setProjectModels] = useState([]);
    const [allModels, setAllModels] = useState([]);
    const [selectedModelId, setSelectedModelId] = useState('');

    const {
        Alert,
        status,
        showSuccess,
        showError,
        handleAlertClose
    } = useAlert();

    const showErrorRef = useRef(showError);
    const handleAlertCloseRef = useRef(handleAlertClose);
    useEffect(() => { showErrorRef.current = showError; }, [showError]);
    useEffect(() => { handleAlertCloseRef.current = handleAlertClose; }, [handleAlertClose]);

    const fetchProjectModels = useCallback(() => {
        llmModelService.getProjectLlmModels(selectedRow.id)
            .then(response => {
                setProjectModels(response.data.items || []);
            })
            .catch(error => {
                showErrorRef.current(error.response?.data?.message || "Error fetching project LLM models");
            });
    }, [selectedRow]);

    const fetchAllModels = useCallback(() => {
        llmModelService.getLlmModels()
            .then(response => {
                setAllModels(response.data.items || []);
            })
            .catch(error => {
                showErrorRef.current(error.response?.data?.message || "Error fetching LLM models");
            });
    }, []);

    useEffect(() => {
        if (open && selectedRow) {
            handleAlertCloseRef.current();
            fetchProjectModels();
            fetchAllModels();
        }
    }, [open, selectedRow, fetchProjectModels, fetchAllModels]);

    const handleClose = () => {
        setOpen(false);
        setSelectedRow(null);
    };

    const handleBindModel = () => {
        if (!selectedModelId) return;
        llmModelService.bindProjectLlmModel(selectedRow.id, selectedModelId)
            .then(() => {
                showSuccess("Model bound successfully");
                fetchProjectModels();
                setSelectedModelId('');
            })
            .catch(error => {
                showError(error.response?.data?.message || "Error binding model");
            });
    };

    const handleUnbindModel = (modelId) => {
        llmModelService.unbindProjectLlmModel(selectedRow.id, modelId)
            .then(() => {
                showSuccess("Model unbound successfully");
                fetchProjectModels();
            })
            .catch(error => {
                showError(error.response?.data?.message || "Error unbinding model");
            });
    };

    const handleMove = (index, direction) => {
        const newModels = [...projectModels];
        const item = newModels.splice(index, 1)[0];
        newModels.splice(index + direction, 0, item);
        
        const modelIds = newModels.map(m => m.id);
        llmModelService.reorderProjectLlmModels(selectedRow.id, modelIds)
            .then(() => {
                setProjectModels(newModels);
            })
            .catch(error => {
                showError(error.response?.data?.message || "Error reordering models");
            });
    };

    return (
        <React.Fragment>
            {status ? <Alert /> : null}
            <Dialog
                title={`Manage LLM Models for Project: ${selectedRow?.name}`}
                open={open}
                handleClose={handleClose}
                maxWidth="md"
                fullWidth
            >
                <DialogContent>
                    <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'flex-end' }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Add LLM Model</InputLabel>
                            <Select
                                value={selectedModelId}
                                label="Add LLM Model"
                                onChange={(e) => setSelectedModelId(e.target.value)}
                            >
                                {allModels
                                    .filter(m => !projectModels.find(pm => pm.id === m.id))
                                    .map(m => (
                                        <MenuItem key={m.id} value={m.id}>
                                            {m.provider.name} - {m.name} ({m.version})
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                        <Button 
                            variant="contained" 
                            startIcon={<AddIcon />}
                            onClick={handleBindModel}
                            disabled={!selectedModelId}
                        >
                            Add
                        </Button>
                    </Box>

                    <Typography variant="h6" gutterBottom>
                        Assigned Models
                    </Typography>
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Provider</TableCell>
                                    <TableCell>Model Name</TableCell>
                                    <TableCell>Version</TableCell>
                                    <TableCell align="center">Order</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {projectModels.map((model, index) => (
                                    <TableRow key={model.id}>
                                        <TableCell>{model.provider.name}</TableCell>
                                        <TableCell>{model.name}</TableCell>
                                        <TableCell>{model.version}</TableCell>
                                        <TableCell align="center">
                                            <IconButton 
                                                size="small" 
                                                disabled={index === 0}
                                                onClick={() => handleMove(index, -1)}
                                            >
                                                <ArrowUpwardIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton 
                                                size="small" 
                                                disabled={index === projectModels.length - 1}
                                                onClick={() => handleMove(index, 1)}
                                            >
                                                <ArrowDownwardIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                        <TableCell align="center">
                                            <IconButton 
                                                size="small" 
                                                color="error"
                                                onClick={() => handleUnbindModel(model.id)}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {projectModels.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center">
                                            No models assigned to this project.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Close</Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
};

export default ProjectLlmModelsDialog;
